import type { Questionnaire } from '@medplum/fhirtypes'
import { VALIDATED_TOOL_TAG } from '#src/models/questionnaires/utils/index.ts'
import { QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL } from '#src/structureDefinitions.ts'

export const gad7Questionnaire = {
  resourceType: 'Questionnaire',
  url: 'https://www.seenhealth.org/fhir/Questionnaire/GAD-7',
  status: 'active',
  subjectType: ['Patient'],
  name: 'GAD-7',
  meta: {
    profile: ['http://hl7.org/fhir/4.0/StructureDefinition/Questionnaire'],
    tag: [VALIDATED_TOOL_TAG, { code: 'lformsVersion: 36.7.0' }],
  },
  title: 'Generalized anxiety disorder 7 item (GAD-7)',
  copyright:
    'Copyright © Pfizer Inc. All rights reserved. Developed by Drs. Robert L. Spitzer, Janet B.W. Williams, Kurt Kroenke and colleagues, with an educational grant from Pfizer Inc. No permission required to reproduce, translate, display or distribute.',
  code: [
    {
      system: 'http://loinc.org',
      code: '69737-5',
      display: 'Generalized anxiety disorder 7 item (GAD-7)',
    },
  ],
  item: [
    {
      linkId: QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL,
      type: 'display',
      text: 'GAD-7 PDF',
      extension: [
        {
          url: QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL,
          valueString:
            'https://adaa.org/sites/default/files/GAD-7_Anxiety-updated_0.pdf',
        },
      ],
    },
    {
      type: 'choice',
      code: [
        {
          code: '69725-0',
          display: 'Feeling nervous, anxious or on edge',
          system: 'http://loinc.org',
        },
      ],
      extension: [
        {
          url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl',
          valueCodeableConcept: {
            coding: [
              {
                system: 'http://hl7.org/fhir/questionnaire-item-control',
                code: 'drop-down',
                display: 'Drop down',
              },
            ],
            text: 'Drop down',
          },
        },
      ],
      linkId: '/69725-0',
      text: 'Feeling nervous, anxious or on edge',
      answerOption: [
        {
          valueCoding: {
            code: 'LA6568-5',
            display: 'Not at all',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '0',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6569-3',
            display: 'Several days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6570-1',
            display: 'More than half the days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 2,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6571-9',
            display: 'Nearly every day',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 3,
            },
          ],
        },
      ],
    },
    {
      type: 'choice',
      code: [
        {
          code: '68509-9',
          display:
            'Over the past 2 weeks have you not been able to stop or control worrying',
          system: 'http://loinc.org',
        },
      ],
      extension: [
        {
          url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl',
          valueCodeableConcept: {
            coding: [
              {
                system: 'http://hl7.org/fhir/questionnaire-item-control',
                code: 'drop-down',
                display: 'Drop down',
              },
            ],
            text: 'Drop down',
          },
        },
      ],
      linkId: '/68509-9',
      text: 'Over the past 2 weeks have you not been able to stop or control worrying',
      answerOption: [
        {
          valueCoding: {
            code: 'LA6568-5',
            display: 'Not at all',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '0',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6569-3',
            display: 'Several days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA18938-3',
            display: 'More days than not',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 2,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6571-9',
            display: 'Nearly every day',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 3,
            },
          ],
        },
      ],
    },
    {
      type: 'choice',
      code: [
        {
          code: '69733-4',
          display: 'Worrying too much about different things',
          system: 'http://loinc.org',
        },
      ],
      extension: [
        {
          url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl',
          valueCodeableConcept: {
            coding: [
              {
                system: 'http://hl7.org/fhir/questionnaire-item-control',
                code: 'drop-down',
                display: 'Drop down',
              },
            ],
            text: 'Drop down',
          },
        },
      ],
      linkId: '/69733-4',
      text: 'Worrying too much about different things',
      answerOption: [
        {
          valueCoding: {
            code: 'LA6568-5',
            display: 'Not at all',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '0',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6569-3',
            display: 'Several days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6570-1',
            display: 'More than half the days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 2,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6571-9',
            display: 'Nearly every day',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 3,
            },
          ],
        },
      ],
    },
    {
      type: 'choice',
      code: [
        {
          code: '69734-2',
          display: 'Trouble relaxing',
          system: 'http://loinc.org',
        },
      ],
      extension: [
        {
          url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl',
          valueCodeableConcept: {
            coding: [
              {
                system: 'http://hl7.org/fhir/questionnaire-item-control',
                code: 'drop-down',
                display: 'Drop down',
              },
            ],
            text: 'Drop down',
          },
        },
      ],
      linkId: '/69734-2',
      text: 'Trouble relaxing',
      answerOption: [
        {
          valueCoding: {
            code: 'LA6568-5',
            display: 'Not at all',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '0',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6569-3',
            display: 'Several days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6570-1',
            display: 'More than half the days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 2,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6571-9',
            display: 'Nearly every day',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 3,
            },
          ],
        },
      ],
    },
    {
      type: 'choice',
      code: [
        {
          code: '69735-9',
          display: 'Being so restless that it is hard to sit still',
          system: 'http://loinc.org',
        },
      ],
      extension: [
        {
          url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl',
          valueCodeableConcept: {
            coding: [
              {
                system: 'http://hl7.org/fhir/questionnaire-item-control',
                code: 'drop-down',
                display: 'Drop down',
              },
            ],
            text: 'Drop down',
          },
        },
      ],
      linkId: '/69735-9',
      text: 'Being so restless that it is hard to sit still',
      answerOption: [
        {
          valueCoding: {
            code: 'LA6568-5',
            display: 'Not at all',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '0',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6569-3',
            display: 'Several days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6570-1',
            display: 'More than half the days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 2,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6571-9',
            display: 'Nearly every day',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 3,
            },
          ],
        },
      ],
    },
    {
      type: 'choice',
      code: [
        {
          code: '69689-8',
          display: 'Becoming easily annoyed or irritable.',
          system: 'http://loinc.org',
        },
      ],
      extension: [
        {
          url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl',
          valueCodeableConcept: {
            coding: [
              {
                system: 'http://hl7.org/fhir/questionnaire-item-control',
                code: 'drop-down',
                display: 'Drop down',
              },
            ],
            text: 'Drop down',
          },
        },
      ],
      linkId: '/69689-8',
      text: 'Becoming easily annoyed or irritable.',
      answerOption: [
        {
          valueCoding: {
            code: 'LA6568-5',
            display: 'Not at all',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '0',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6569-3',
            display: 'Several days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6570-1',
            display: 'More than half the days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 2,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6571-9',
            display: 'Nearly every day',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 3,
            },
          ],
        },
      ],
    },
    {
      type: 'choice',
      code: [
        {
          code: '69736-7',
          display: 'Feeling afraid as if something awful might happen',
          system: 'http://loinc.org',
        },
      ],
      extension: [
        {
          url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl',
          valueCodeableConcept: {
            coding: [
              {
                system: 'http://hl7.org/fhir/questionnaire-item-control',
                code: 'drop-down',
                display: 'Drop down',
              },
            ],
            text: 'Drop down',
          },
        },
      ],
      linkId: '/69736-7',
      text: 'Feeling afraid as if something awful might happen',
      answerOption: [
        {
          valueCoding: {
            code: 'LA6568-5',
            display: 'Not at all',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '0',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6569-3',
            display: 'Several days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6570-1',
            display: 'More than half the days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 2,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6571-9',
            display: 'Nearly every day',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 3,
            },
          ],
        },
      ],
    },
    {
      item: [
        {
          text: 'If you checked off any problems on this questionnaire',
          type: 'display',
          linkId: '/69722-7-help',
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl',
              valueCodeableConcept: {
                text: 'Help-Button',
                coding: [
                  {
                    code: 'help',
                    display: 'Help-Button',
                    system: 'http://hl7.org/fhir/questionnaire-item-control',
                  },
                ],
              },
            },
          ],
        },
      ],
      type: 'choice',
      extension: [
        {
          url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl',
          valueCodeableConcept: {
            coding: [
              {
                system: 'http://hl7.org/fhir/questionnaire-item-control',
                code: 'drop-down',
                display: 'Drop down',
              },
            ],
            text: 'Drop down',
          },
        },
      ],
      linkId: '/69722-7',
      code: [
        {
          system: 'http://loinc.org',
          code: '69722-7',
          display:
            'How difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?',
        },
      ],
      text: 'How difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?',
      answerOption: [
        {
          valueCoding: {
            system: 'http://loinc.org',
            code: 'LA6572-7',
            display: 'Not difficult at all',
          },
        },
        {
          valueCoding: {
            system: 'http://loinc.org',
            code: 'LA6573-5',
            display: 'Somewhat difficult',
          },
        },
        {
          valueCoding: {
            system: 'http://loinc.org',
            code: 'LA6575-0',
            display: 'Very difficult',
          },
        },
        {
          valueCoding: {
            system: 'http://loinc.org',
            code: 'LA6574-3',
            display: 'Extremely difficult',
          },
        },
      ],
    },
    {
      type: 'decimal',
      code: [
        {
          code: '70274-6',
          display: 'Generalized anxiety disorder 7 item total score',
          system: 'http://loinc.org',
        },
      ],
      extension: [
        {
          url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-unit',
          valueCoding: {
            code: '{score}',
            display: '{score}',
            system: 'http://unitsofmeasure.org',
          },
        },
      ],
      linkId: '/70274-6',
      text: 'Generalized anxiety disorder 7 item total score',
    },
  ],
} satisfies Questionnaire

export type Gad7Category =
  | 'Severe Anxiety'
  | 'Moderate Anxiety'
  | 'Mild Anxiety'
  | 'Minimal Anxiety'

export function gad7Category(score: number): Gad7Category | undefined {
  if (score >= 15) {
    return 'Severe Anxiety'
  }
  if (score >= 10 && score <= 14) {
    return 'Moderate Anxiety'
  }
  if (score >= 5 && score <= 9) {
    return 'Mild Anxiety'
  }
  if (score >= 0 && score <= 4) {
    return 'Minimal Anxiety'
  }
  return undefined
}
